import { useEffect, useRef } from 'react';

import {
  applyBuilderEntry,
  BuilderEntryTarget,
} from '../stores/utils/table-store-builder-entry';

/**
 * PSTUDIO-4240: the Workflows page's ENTRY effect — the entrance Builder's
 * assignment effect cannot be.
 *
 * That effect can only ASSIGN, and assigning an unchanged id is a no-op for the
 * reaction that issues the fetch. So a return into the page that never released
 * the identity (browser Back onto `/workflows`; Workflows clicked from a page
 * inside the same solution, neither of which dispatches `selectBot`) left a
 * store parked in the waiting state exactly as it found it: 0 nodes, no
 * spinner, and Publish behind "We're still setting things up" until a hard
 * reload.
 *
 * It takes the store as an argument rather than importing it. That is what
 * makes this file testable: `stores/table-store` pulls in
 * `@pypestream/design-system`, whose Lit custom-element registration is what
 * actually breaks a jsdom render of anything that reaches it — the package has
 * a working React harness otherwise (16 `*.test.tsx` files use
 * `// @vitest-environment jsdom` with `react-dom/client` + `act`). The WHETHER
 * lives in `applyBuilderEntry`; this hook owns only the WHEN.
 *
 * Two properties of the wiring, both of them load-bearing and both pinned by
 * `__tests__/use-builder-entry.test.tsx`:
 *
 * - **It must be declared AFTER the assignment effect** in `pages/builder.tsx`.
 *   The reaction runs synchronously (`stores/table-store.ts`, the reaction's
 *   `delay: 0` — mobx turns a falsy delay into `runSync`), so by the time this
 *   hook reads the store, an entrance that DID move the identity has already
 *   been through the reaction's forced cleanup, which lowers both waiting
 *   flags. That — not the id comparison — is what makes `applyBuilderEntry`
 *   refuse on a genuine version switch.
 * - **It must not re-fire for an entrance it has already served.**
 *   `restartVersionVisit` deliberately leaves `isWaitingForBackendData` raised,
 *   so the rule's precondition is still true while the fetch is in flight, and
 *   a replayed mount would restart the visit a second time. `main.tsx` wraps
 *   the app in `StrictMode` unconditionally, which replays every mount effect
 *   in dev; measured before this ref existed, one entrance issued two
 *   `GetProStudioVersionData` requests. The ref keys on the id, so a later
 *   entrance on a DIFFERENT id is still served.
 *
 * Keyed on `solutionVersionId` rather than mount-only on purpose: after a POP
 * the first frames arrive before the XState context resolves, and the id lands
 * a render or two later.
 */
export function useBuilderEntry(
  store: BuilderEntryTarget,
  solutionVersionId: string
): void {
  const restartedFor = useRef<string | null>(null);

  useEffect(() => {
    if (restartedFor.current === solutionVersionId) {
      return;
    }

    if (applyBuilderEntry(store, { solutionVersionId })) {
      restartedFor.current = solutionVersionId;
    }
    // `store` is a module singleton in production; re-running on its identity
    // would add nothing and would make the replay guard above depend on it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solutionVersionId]);
}
